import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import {
	approveTranscription,
	submitAnswers,
	submitVoiceAnswers,
	updateTranscription,
	uploadVoiceAnswer,
} from "../api/rounds";
import {
	deleteStory,
	getFinalRecordingUrl,
	getStory,
	markDone,
	restartStory,
	retryStory,
	updateStoryText,
	updateStoryTitle,
} from "../api/stories";
import { AudioRecorder } from "../components/AudioRecorder";
import { CompactAudioPlayer } from "../components/CompactAudioPlayer";
import { InkDivider } from "../components/InkDivider";
import { RisographBackground } from "../components/RisographBackground";
import { SourceMaterialModal } from "../components/SourceMaterialModal";
import { TagEditor } from "../components/TagEditor";
import { getInputModePref } from "../hooks/useInputModePref";
import { useRoundPolling } from "../hooks/useRoundPolling";
import { useVoiceAnswerPolling } from "../hooks/useVoiceAnswerPolling";
import {
	attemptUpload,
	deleteRecording,
	getRecording,
	saveRecording,
} from "../lib/recordingStorage";
import type {
	AudioUrlResponse,
	QuestionAnswer,
	Round,
	StoryDetail,
} from "../types";

const ACCENT_COLORS = ["#4A7DE8", "#E84B6A", "#3ED8C3", "#A855F7", "#F97316"];

function accentForId(id: string): string {
	let hash = 0;
	for (let i = 0; i < id.length; i++) {
		hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
	}
	return ACCENT_COLORS[hash % ACCENT_COLORS.length] ?? "#4A7DE8";
}

// ── DismissableNote ───────────────────────────────────────────────────────────

function DismissableNote({
	feedback,
	label,
	visible,
	onDismiss,
	onShow,
}: {
	feedback: string;
	label: string;
	visible: boolean;
	onDismiss: () => void;
	onShow: () => void;
}) {
	const { t } = useTranslation();
	return (
		<div>
			{/* Note — collapses via CSS grid rows trick */}
			<div
				style={{
					display: "grid",
					gridTemplateRows: visible ? "1fr" : "0fr",
					marginBottom: visible ? "2rem" : "0",
					transition: "grid-template-rows 0.35s ease, margin-bottom 0.35s ease",
				}}
			>
				<div style={{ overflow: "hidden" }}>
					<div className="relative bg-white/30 backdrop-blur-sm rounded-sm p-6">
						<button
							type="button"
							onClick={onDismiss}
							className="absolute top-3 right-3 text-ink/25 hover:text-ink/60 transition-colors p-1"
							aria-label="Dismiss note"
						>
							<svg
								width="12"
								height="12"
								viewBox="0 0 12 12"
								fill="none"
								aria-hidden="true"
							>
								<path
									d="M1 1l10 10M11 1L1 11"
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
								/>
							</svg>
						</button>
						<p className="font-ui text-[10px] uppercase tracking-widest text-ink/40 mb-3">
							{label}
						</p>
						<p className="font-body italic text-ink/70 text-base leading-relaxed pr-4">
							{feedback}
						</p>
					</div>
				</div>
			</div>

			{/* Show button — fades in once note has collapsed */}
			<div
				style={{
					display: "grid",
					gridTemplateRows: visible ? "0fr" : "1fr",
					marginBottom: visible ? "0" : "2rem",
					transition:
						"grid-template-rows 0.25s ease 0.15s, margin-bottom 0.25s ease 0.15s",
				}}
			>
				<div style={{ overflow: "hidden" }}>
					<button
						type="button"
						onClick={onShow}
						className="font-ui text-[10px] uppercase tracking-widest text-ink/35 hover:text-ink/60 transition-colors"
					>
						{t("stories.detail.coaching.showNote", { label })}
					</button>
				</div>
			</div>
		</div>
	);
}

// ── HamburgerMenu ─────────────────────────────────────────────────────────────

interface MenuAction {
	label: string;
	onClick: () => void;
	disabled?: boolean;
	destructive?: boolean;
	dividerBefore?: boolean;
}

function HamburgerMenu({ actions }: { actions: MenuAction[] }) {
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) return;
		function handleClick(e: MouseEvent) {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setOpen(false);
			}
		}
		document.addEventListener("mousedown", handleClick);
		return () => document.removeEventListener("mousedown", handleClick);
	}, [open]);

	return (
		<div ref={ref} className="relative">
			<button
				type="button"
				onClick={() => setOpen((o) => !o)}
				className="font-ui text-xl text-ink/40 hover:text-ink/70 transition-colors leading-none px-1"
				aria-label="Story actions"
				aria-expanded={open}
			>
				≡
			</button>
			{open && (
				<div className="absolute right-0 top-full mt-2 min-w-[200px] bg-cream border border-ink/10 rounded-sm shadow-lg z-50 py-1">
					{actions.map((action) => (
						<div key={action.label}>
							{action.dividerBefore && (
								<div className="my-1 border-t border-ink/8" />
							)}
							<button
								type="button"
								disabled={action.disabled}
								onClick={() => {
									action.onClick();
									setOpen(false);
								}}
								className={`w-full text-left px-4 py-3 font-ui text-[10px] uppercase tracking-widest hover:bg-ink/5 transition-colors disabled:opacity-40 ${
									action.destructive
										? "text-coral/70 hover:text-coral"
										: "text-ink/55 hover:text-ink"
								}`}
							>
								{action.label}
							</button>
						</div>
					))}
				</div>
			)}
		</div>
	);
}

// ── ConfirmDeleteModal ────────────────────────────────────────────────────────

function ConfirmDeleteModal({
	onConfirm,
	onCancel,
	deleting,
}: {
	onConfirm: () => void;
	onCancel: () => void;
	deleting: boolean;
}) {
	const { t } = useTranslation();
	const headingId = useId();
	const dialogRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const dialog = dialogRef.current;
		if (!dialog) return;

		const previously = document.activeElement as HTMLElement | null;
		const focusable = dialog.querySelectorAll<HTMLElement>(
			'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
		);
		const first = focusable[0];
		const last = focusable[focusable.length - 1];
		first?.focus();

		function handleKey(e: KeyboardEvent) {
			if (e.key === "Escape") {
				onCancel();
				return;
			}
			if (e.key !== "Tab") return;
			if (focusable.length === 0) {
				e.preventDefault();
				return;
			}
			if (e.shiftKey) {
				if (document.activeElement === first) {
					e.preventDefault();
					last?.focus();
				}
			} else {
				if (document.activeElement === last) {
					e.preventDefault();
					first?.focus();
				}
			}
		}

		document.addEventListener("keydown", handleKey);
		return () => {
			document.removeEventListener("keydown", handleKey);
			previously?.focus();
		};
	}, [onCancel]);

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center px-6">
			<div
				className="absolute inset-0 bg-ink/30 backdrop-blur-sm"
				aria-hidden="true"
				onClick={onCancel}
			/>
			<div
				ref={dialogRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby={headingId}
				className="relative bg-cream border border-ink/15 rounded-sm shadow-xl max-w-sm w-full p-8"
			>
				<h2
					id={headingId}
					className="font-display text-xl font-bold text-ink mb-3"
				>
					{t("stories.detail.deleteConfirm.heading")}
				</h2>
				<p className="font-body italic text-ink/60 text-sm leading-relaxed mb-8">
					{t("stories.detail.deleteConfirm.body")}
				</p>
				<div className="flex items-center justify-between">
					<button
						type="button"
						onClick={onCancel}
						disabled={deleting}
						className="font-ui text-[10px] uppercase tracking-widest text-ink/40 hover:text-ink/70 transition-colors disabled:opacity-40"
					>
						{t("stories.detail.deleteConfirm.cancel")}
					</button>
					<button
						type="button"
						onClick={onConfirm}
						disabled={deleting}
						className="ink-btn px-6 py-2 font-ui text-xs uppercase tracking-widest disabled:opacity-40"
						style={{ background: "#E84B6A" }}
					>
						{deleting
							? t("stories.detail.deleteConfirm.deleting")
							: t("stories.detail.deleteConfirm.confirm")}
					</button>
				</div>
			</div>
		</div>
	);
}

// ── Processing sub-view ────────────────────────────────────────────────────────
function ProcessingView({ story }: { story: StoryDetail }) {
	const { t } = useTranslation();
	const round = useRoundPolling(story.active_round_id);
	const isTranscribing = round.data?.status === "transcribing";

	return (
		<div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
			<RisographBackground />
			<div
				role="status"
				aria-live="polite"
				className="flex flex-col items-center"
			>
				<div className="mb-8">
					<InkDot />
				</div>
				<p className="font-ui text-xs uppercase tracking-[0.25em] text-ink/50 mb-3">
					{isTranscribing
						? t("stories.detail.processing.transcribing")
						: t("stories.detail.processing.reading")}
				</p>
				{story.title && (
					<p className="font-body italic text-ink/40 text-sm">{story.title}</p>
				)}
			</div>
		</div>
	);
}

function InkDot() {
	return (
		<div className="flex gap-2 items-center">
			{[0, 1, 2].map((i) => (
				<span
					key={i}
					className="w-2 h-2 rounded-full bg-ink/40"
					style={{
						animation: `pulse 1.4s ease-in-out ${i * 0.2}s infinite`,
					}}
				/>
			))}
		</div>
	);
}

// ── Coaching sub-view ──────────────────────────────────────────────────────────
function CoachingView({ story, round }: { story: StoryDetail; round: Round }) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const questions = round.questions ?? [];

	const [phase, setPhase] = useState<"recording" | "reviewing">("recording");
	const [step, setStep] = useState(0);
	const [noteVisible, setNoteVisible] = useState(true);
	const [inputMode, setInputMode] = useState<"text" | "voice">(
		getInputModePref,
	);
	const [questionModes, setQuestionModes] = useState<
		Record<string, "text" | "voice">
	>({});
	const [answers, setAnswers] = useState<Record<string, string>>({});
	const [voiceBlobs, setVoiceBlobs] = useState<Record<string, Blob>>({});
	const [uploading, setUploading] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [voiceUploadFailed, setVoiceUploadFailed] = useState(false);
	const [imDoneConfirming, setImDoneConfirming] = useState(false);

	const { statuses: voiceStatuses, refresh: refreshPolling } =
		useVoiceAnswerPolling(round.id);

	// On mount: restore any voice blobs from a previous session that failed to upload.
	const hasRestoredIdbRef = useRef(false);
	useEffect(() => {
		if (hasRestoredIdbRef.current || questions.length === 0) return;
		hasRestoredIdbRef.current = true;
		let cancelled = false;

		async function restore() {
			const restoredBlobs: Record<string, Blob> = {};
			const restoredModes: Record<string, "voice"> = {};
			for (const q of questions) {
				const stored = await getRecording(`voice:${round.id}:${q.id}`);
				if (stored && stored.failCount < 3) {
					restoredBlobs[q.id] = stored.blob;
					restoredModes[q.id] = "voice";
				}
			}
			if (!cancelled && Object.keys(restoredBlobs).length > 0) {
				setVoiceBlobs((prev) => ({ ...restoredBlobs, ...prev }));
				setQuestionModes((prev) => ({ ...restoredModes, ...prev }));
			}
		}

		restore();
		return () => {
			cancelled = true;
		};
	}, [questions, round.id]);

	// Persist voice blobs to IDB whenever they're recorded.
	useEffect(() => {
		for (const [qId, blob] of Object.entries(voiceBlobs)) {
			saveRecording(`voice:${round.id}:${qId}`, blob).catch(() => {});
		}
	}, [voiceBlobs, round.id]);

	const hasRestoredRef = useRef(false);
	useEffect(() => {
		if (hasRestoredRef.current || voiceStatuses.size === 0) return;
		hasRestoredRef.current = true;
		const hasServerVoice = [...voiceStatuses.values()].some(
			(s) =>
				s.status === "pending" ||
				s.status === "transcribing" ||
				s.status === "ready",
		);
		if (hasServerVoice) setPhase("reviewing");
	}, [voiceStatuses]);

	useEffect(() => {
		for (const [qId, s] of voiceStatuses) {
			if (s.status === "ready" && s.transcribed_text) {
				setAnswers((prev) => {
					if (qId in prev) return prev;
					return { ...prev, [qId]: s.transcribed_text ?? "" };
				});
			}
		}
	}, [voiceStatuses]);

	const question = questions[step];
	const isLast = step === questions.length - 1;
	const accent = ACCENT_COLORS[step % ACCENT_COLORS.length] ?? "#4A7DE8";
	const currentMode = question
		? (questionModes[question.id] ?? inputMode)
		: inputMode;
	const hasAnyVoiceBlob = Object.keys(voiceBlobs).length > 0;

	function setQuestionMode(mode: "text" | "voice") {
		setInputMode(mode);
		if (!question) return;
		setQuestionModes((prev) => ({ ...prev, [question.id]: mode }));
		if (mode === "text") {
			setVoiceBlobs((prev) => {
				const next = { ...prev };
				delete next[question.id];
				return next;
			});
		}
	}

	function clearQuestion(qId: string) {
		deleteRecording(`voice:${round.id}:${qId}`).catch(() => {});
		setVoiceBlobs((prev) => {
			const next = { ...prev };
			delete next[qId];
			return next;
		});
		setAnswers((prev) => {
			const next = { ...prev };
			delete next[qId];
			return next;
		});
	}

	async function handleDone() {
		if (!hasAnyVoiceBlob) {
			await doSubmit();
			return;
		}
		setUploading(true);
		setVoiceUploadFailed(false);

		const results = await Promise.all(
			Object.entries(voiceBlobs).map(([qId, blob]) =>
				attemptUpload(`voice:${round.id}:${qId}`, () =>
					uploadVoiceAnswer(round.id, qId, blob),
				),
			),
		);

		setUploading(false);
		if (results.some((r) => r === "exceeded")) {
			setVoiceUploadFailed(true);
		}
		refreshPolling();
		setPhase("reviewing");
	}

	async function doSubmit() {
		setSubmitting(true);
		try {
			// Pure voice session: use server-side transcriptions via the dedicated endpoint.
			// Mixed (some text, some voice) or text-only: send client answers as text so
			// manually typed answers are preserved.
			const hasMixedTextAnswers = questions.some(
				(q) => !voiceStatuses.has(q.id) && (answers[q.id] ?? "") !== "",
			);
			if (voiceStatuses.size > 0 && !hasMixedTextAnswers) {
				await submitVoiceAnswers(round.id);
			} else {
				const payload: QuestionAnswer[] = questions.map((q) => ({
					question_id: q.id,
					text: answers[q.id] ?? "",
				}));
				await submitAnswers(round.id, payload);
			}
			queryClient.invalidateQueries({ queryKey: ["story", story.id] });
			queryClient.invalidateQueries({ queryKey: ["round", round.id] });
			navigate(`/stories/${story.id}`);
		} finally {
			setSubmitting(false);
		}
	}

	async function handleMarkDone() {
		await markDone(story.id);
		queryClient.invalidateQueries({ queryKey: ["story", story.id] });
		navigate(`/stories/${story.id}`);
	}

	// ── Review phase ─────────────────────────────────────────────────────────────
	if (phase === "reviewing") {
		const transcribingCount = [...voiceStatuses.values()].filter(
			(s) => s.status === "pending" || s.status === "transcribing",
		).length;

		return (
			<div className="min-h-screen flex flex-col px-6 py-12 max-w-2xl mx-auto">
				<RisographBackground />

				{round.latest_version?.coaching_feedback && (
					<DismissableNote
						feedback={round.latest_version.coaching_feedback}
						label={t("stories.detail.coaching.akinNotes")}
						visible={noteVisible}
						onDismiss={() => setNoteVisible(false)}
						onShow={() => setNoteVisible(true)}
					/>
				)}

				<InkDivider className="mb-8" />

				{voiceUploadFailed && (
					<div
						role="alert"
						className="mb-6 bg-coral/8 border border-coral/30 rounded-sm px-4 py-4"
					>
						<p className="font-ui text-[10px] uppercase tracking-widest text-coral mb-3 flex items-center gap-2">
							<svg
								width="12"
								height="12"
								viewBox="0 0 12 12"
								fill="none"
								aria-hidden="true"
							>
								<path
									d="M6 1L11 10H1L6 1z"
									stroke="currentColor"
									strokeWidth="1.2"
									strokeLinejoin="round"
								/>
								<path
									d="M6 5v2.5M6 9h.01"
									stroke="currentColor"
									strokeWidth="1.2"
									strokeLinecap="round"
								/>
							</svg>
							{t("stories.detail.coaching.voiceUploadFailed")}
						</p>
						<button
							type="button"
							onClick={async () => {
								setVoiceUploadFailed(false);
								const results = await Promise.all(
									Object.entries(voiceBlobs).map(([qId, blob]) =>
										attemptUpload(`voice:${round.id}:${qId}`, () =>
											uploadVoiceAnswer(round.id, qId, blob),
										),
									),
								);
								if (results.some((r) => r === "exceeded")) {
									setVoiceUploadFailed(true);
								} else {
									refreshPolling();
								}
							}}
							className="ink-btn px-4 py-2 font-ui text-[10px] uppercase tracking-widest"
						>
							{t("stories.detail.coaching.retryVoiceUpload")}
						</button>
					</div>
				)}

				{transcribingCount > 0 && (
					<div className="flex items-center gap-3 mb-8">
						<InkDot />
						<p className="font-ui text-xs uppercase tracking-widest text-ink/40">
							{t("stories.detail.coaching.transcribing", {
								count: transcribingCount,
							})}
						</p>
					</div>
				)}

				<div className="flex flex-col gap-8 flex-1 mb-10">
					{questions.map((q, i) => {
						const vStatus = voiceStatuses.get(q.id);
						const isTranscribing =
							vStatus?.status === "pending" ||
							vStatus?.status === "transcribing";
						const isFailed = vStatus?.status === "failed";
						const qAccent =
							ACCENT_COLORS[i % ACCENT_COLORS.length] ?? "#4A7DE8";

						return (
							<div
								key={q.id}
								className="pl-4"
								style={{ borderLeft: `2px solid ${qAccent}` }}
							>
								<p className="font-body text-ink/70 text-base leading-snug mb-3">
									{q.text}
								</p>
								{isTranscribing ? (
									<div className="flex items-center gap-2 py-3">
										<InkDot />
									</div>
								) : (
									<textarea
										value={answers[q.id] ?? ""}
										onChange={(e) =>
											setAnswers((prev) => ({
												...prev,
												[q.id]: e.target.value,
											}))
										}
										placeholder={
											isFailed
												? t("stories.detail.coaching.transcriptionFailed")
												: t("stories.detail.coaching.leaveBlank")
										}
										className="w-full min-h-[80px] bg-white/20 backdrop-blur-sm border border-ink/10 rounded-sm p-3 font-body text-ink/80 text-base leading-relaxed outline-none resize-none placeholder-ink/25 focus:bg-white/30 transition-colors"
									/>
								)}
								{isFailed && (
									<p className="font-ui text-[10px] uppercase tracking-wide text-coral/70 mt-1">
										{t("stories.detail.coaching.transcriptionFailedNote")}
									</p>
								)}
							</div>
						);
					})}
				</div>

				<div className="flex items-center justify-between">
					<button
						type="button"
						onClick={doSubmit}
						disabled={submitting || transcribingCount > 0}
						className="ink-btn px-8 py-3 font-ui text-sm uppercase tracking-widest disabled:opacity-40"
					>
						{submitting
							? t("stories.detail.coaching.submitting")
							: transcribingCount > 0
								? t("stories.detail.coaching.transcribing", {
										count: transcribingCount,
									})
								: t("stories.detail.coaching.submit")}
					</button>
					{imDoneConfirming ? (
						<div className="flex flex-col items-end gap-2">
							<p className="font-ui text-[10px] uppercase tracking-widest text-ink/50 text-right">
								{t("stories.detail.coaching.imDoneConfirm")}
							</p>
							<div className="flex gap-3">
								<button
									type="button"
									onClick={() => setImDoneConfirming(false)}
									className="font-ui text-[10px] uppercase tracking-widest text-ink/40 hover:text-ink/70 transition-colors"
								>
									{t("stories.detail.coaching.imDoneCancel")}
								</button>
								<button
									type="button"
									onClick={handleMarkDone}
									className="font-ui text-[10px] uppercase tracking-widest text-coral hover:text-coral/70 transition-colors"
								>
									{t("stories.detail.coaching.imDoneYes")}
								</button>
							</div>
						</div>
					) : (
						<button
							type="button"
							onClick={() => setImDoneConfirming(true)}
							className="font-ui text-[10px] uppercase tracking-widest text-ink/30 hover:text-ink/60 transition-colors"
						>
							{t("stories.detail.coaching.imDone")}
						</button>
					)}
				</div>
			</div>
		);
	}

	// ── Recording phase ──────────────────────────────────────────────────────────
	if (!question) return null;

	function handleSkip() {
		clearQuestion(question.id);
		if (isLast) handleDone();
		else setStep((s) => s + 1);
	}

	return (
		<div className="min-h-screen flex flex-col px-6 py-12 max-w-2xl mx-auto">
			<RisographBackground />

			{/* Header: story title left, exit right */}
			<div className="flex items-center justify-between mb-6 min-h-[44px]">
				<p className="font-ui text-[10px] uppercase tracking-[0.25em] text-ink/40 flex-1 mr-4 truncate">
					{story.title ?? ""}
				</p>
				{imDoneConfirming ? (
					<div className="flex items-center gap-1 shrink-0">
						<button
							type="button"
							onClick={() => setImDoneConfirming(false)}
							className="font-ui text-[10px] uppercase tracking-widest text-ink/40 hover:text-ink/70 transition-colors min-h-[44px] px-2"
						>
							{t("stories.detail.coaching.imDoneCancel")}
						</button>
						<span className="text-ink/20 select-none">·</span>
						<button
							type="button"
							onClick={handleMarkDone}
							className="font-ui text-[10px] uppercase tracking-widest text-coral hover:text-coral/70 transition-colors min-h-[44px] px-2"
						>
							{t("stories.detail.coaching.imDoneYes")}
						</button>
					</div>
				) : (
					<button
						type="button"
						onClick={() => setImDoneConfirming(true)}
						className="font-ui text-[10px] uppercase tracking-widest text-ink/25 hover:text-ink/50 transition-colors shrink-0 min-h-[44px] flex items-center"
					>
						{t("stories.detail.coaching.imDone")}
					</button>
				)}
			</div>

			{round.latest_version?.coaching_feedback && (
				<DismissableNote
					feedback={round.latest_version.coaching_feedback}
					label={t("stories.detail.coaching.akinNotes")}
					visible={noteVisible}
					onDismiss={() => setNoteVisible(false)}
					onShow={() => setNoteVisible(true)}
				/>
			)}

			<InkDivider className="mb-10" />

			{/* Progress: dots + numeric counter */}
			<div className="flex items-center gap-3 mb-8">
				<div
					className="flex gap-2"
					role="group"
					aria-label={t("stories.detail.coaching.questionStep", {
						step: step + 1,
						total: questions.length,
					})}
				>
					{questions.map((q, i) => {
						const answered = !!(answers[q.id] || voiceBlobs[q.id]);
						return (
							<span
								key={q.id}
								className="w-2 h-2 rounded-full transition-colors"
								aria-label={t("stories.detail.coaching.questionStep", {
									step: i + 1,
									total: questions.length,
								})}
								aria-current={i === step ? "step" : undefined}
								style={{
									background: answered || i === step ? accent : "#1a120930",
								}}
							/>
						);
					})}
				</div>
				<span className="font-ui text-[10px] uppercase tracking-widest text-ink/25">
					{step + 1} / {questions.length}
				</span>
			</div>

			{/* Question + input — Skip lives here, beside the mode-switch */}
			<div
				className="flex-1 pl-5 mb-8"
				style={{ borderLeft: `3px solid ${accent}` }}
			>
				<p className="font-display text-3xl font-bold text-ink leading-snug mb-8">
					{question.text}
				</p>

				{currentMode === "text" ? (
					<div className="flex flex-col gap-3">
						<textarea
							value={answers[question.id] ?? ""}
							onChange={(e) =>
								setAnswers((prev) => ({
									...prev,
									[question.id]: e.target.value,
								}))
							}
							placeholder={t("stories.detail.coaching.takeYourTime")}
							className="w-full min-h-[160px] bg-white/20 backdrop-blur-sm border border-ink/10 rounded-sm p-4 font-body text-ink/80 text-base leading-relaxed outline-none resize-none placeholder-ink/25 focus:bg-white/30 transition-colors"
						/>
						<div className="flex items-center justify-between">
							<button
								type="button"
								onClick={() => setQuestionMode("voice")}
								className="font-ui text-[10px] uppercase tracking-widest text-ink/30 hover:text-ink/55 transition-colors min-h-[44px] flex items-center"
							>
								{t("stories.detail.coaching.switchToVoice")}
							</button>
							<button
								type="button"
								onClick={handleSkip}
								disabled={uploading}
								className="border border-ink/15 rounded-sm px-4 py-2 font-ui text-[10px] uppercase tracking-widest text-ink/40 hover:border-ink/30 hover:text-ink/60 transition-colors disabled:opacity-40"
							>
								{t("stories.detail.coaching.skip")}
							</button>
						</div>
					</div>
				) : (
					<div className="flex flex-col gap-3">
						<AudioRecorder
							key={question.id}
							onRecordingComplete={(blob) =>
								setVoiceBlobs((prev) => ({ ...prev, [question.id]: blob }))
							}
							onReset={() =>
								setVoiceBlobs((prev) => {
									const next = { ...prev };
									delete next[question.id];
									return next;
								})
							}
						/>
						<div className="flex items-center justify-between">
							<button
								type="button"
								onClick={() => setQuestionMode("text")}
								className="font-ui text-[10px] uppercase tracking-widest text-ink/30 hover:text-ink/55 transition-colors min-h-[44px] flex items-center"
							>
								{t("stories.detail.coaching.switchToText")}
							</button>
							<button
								type="button"
								onClick={handleSkip}
								disabled={uploading}
								className="border border-ink/15 rounded-sm px-4 py-2 font-ui text-[10px] uppercase tracking-widest text-ink/40 hover:border-ink/30 hover:text-ink/60 transition-colors disabled:opacity-40"
							>
								{t("stories.detail.coaching.skip")}
							</button>
						</div>
					</div>
				)}
			</div>

			{/* Bottom nav: stable layout, Back always reserves space */}
			<div className="flex items-center gap-3">
				<button
					type="button"
					onClick={() => setStep((s) => s - 1)}
					className={`border border-ink/20 rounded-sm px-6 py-3 font-ui text-xs uppercase tracking-widest text-ink/50 hover:border-ink/40 hover:text-ink/70 transition-colors ${
						step === 0 ? "invisible pointer-events-none" : ""
					}`}
					tabIndex={step === 0 ? -1 : 0}
					aria-hidden={step === 0 ? true : undefined}
				>
					{t("stories.detail.coaching.back")}
				</button>
				{isLast ? (
					<button
						type="button"
						onClick={handleDone}
						disabled={uploading}
						className="ink-btn px-8 py-3 font-ui text-sm uppercase tracking-widest disabled:opacity-40"
					>
						{uploading
							? t("stories.detail.coaching.uploading")
							: hasAnyVoiceBlob
								? t("stories.detail.coaching.doneSendAnswers")
								: t("stories.detail.coaching.reviewAndSubmit")}
					</button>
				) : (
					<button
						type="button"
						onClick={() => setStep((s) => s + 1)}
						className="ink-btn px-8 py-3 font-ui text-sm uppercase tracking-widest"
					>
						{t("stories.detail.coaching.next")}
					</button>
				)}
			</div>
		</div>
	);
}

// ── Transcription preview sub-view ────────────────────────────────────────────
function TranscriptionPreviewView({
	story,
	round,
}: {
	story: StoryDetail;
	round: Round;
}) {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const [text, setText] = useState(round.transcribed_text ?? "");
	const [isEditing, setIsEditing] = useState(false);
	const [saving, setSaving] = useState(false);
	const [approving, setApproving] = useState(false);

	async function handleSave() {
		setSaving(true);
		try {
			await updateTranscription(round.id, text);
			setIsEditing(false);
		} finally {
			setSaving(false);
		}
	}

	async function handleApprove() {
		if (isEditing) {
			await updateTranscription(round.id, text);
			setIsEditing(false);
		}
		setApproving(true);
		try {
			await approveTranscription(round.id);
			queryClient.invalidateQueries({ queryKey: ["story", story.id] });
			queryClient.invalidateQueries({ queryKey: ["round", round.id] });
		} finally {
			setApproving(false);
		}
	}

	return (
		<div className="min-h-screen flex flex-col px-6 py-12 max-w-2xl mx-auto">
			<RisographBackground />

			<p className="font-ui text-[10px] uppercase tracking-[0.25em] text-ink/40 mb-10">
				{story.title ?? "Your story"}
			</p>

			<p className="font-ui text-[10px] uppercase tracking-widest text-ink/40 mb-3">
				{t("stories.detail.transcriptionPreview.heading")}
			</p>

			<div className="bg-white/30 backdrop-blur-sm border border-ink/10 rounded-sm p-8 mb-3">
				{isEditing ? (
					<textarea
						value={text}
						onChange={(e) => setText(e.target.value)}
						className="w-full min-h-[200px] bg-transparent font-body italic text-ink/80 text-lg leading-relaxed outline-none resize-none"
					/>
				) : (
					<p className="font-body italic text-ink/80 text-lg leading-relaxed whitespace-pre-wrap">
						{text || (
							<span className="text-ink/30">
								{t("stories.detail.transcriptionPreview.noTranscription")}
							</span>
						)}
					</p>
				)}
			</div>

			<div className="flex justify-end mb-8">
				{isEditing ? (
					<div className="flex gap-3">
						<button
							type="button"
							onClick={() => {
								setText(round.transcribed_text ?? "");
								setIsEditing(false);
							}}
							className="font-ui text-[10px] uppercase tracking-widest text-ink/35 hover:text-ink/60 transition-colors"
						>
							{t("stories.detail.transcriptionPreview.cancel")}
						</button>
						<button
							type="button"
							onClick={handleSave}
							disabled={saving}
							className="font-ui text-[10px] uppercase tracking-widest text-blue hover:text-blue/70 transition-colors disabled:opacity-40"
						>
							{saving
								? t("stories.detail.transcriptionPreview.saving")
								: t("stories.detail.transcriptionPreview.save")}
						</button>
					</div>
				) : (
					<button
						type="button"
						onClick={() => setIsEditing(true)}
						className="font-ui text-[10px] uppercase tracking-widest text-ink/35 hover:text-ink/60 transition-colors"
					>
						{t("stories.detail.transcriptionPreview.editTranscription")}
					</button>
				)}
			</div>

			<InkDivider className="mb-8" />

			<button
				type="button"
				onClick={handleApprove}
				disabled={approving}
				className="ink-btn self-start px-8 py-3 font-ui text-sm uppercase tracking-widest disabled:opacity-40"
			>
				{approving
					? t("stories.detail.transcriptionPreview.starting")
					: t("stories.detail.transcriptionPreview.looksGood")}
			</button>
		</div>
	);
}

// ── Failed sub-view ────────────────────────────────────────────────────────────
function FailedView({ story }: { story: StoryDetail }) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const [retrying, setRetrying] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

	async function handleRetry() {
		setRetrying(true);
		try {
			await retryStory(story.id);
			queryClient.invalidateQueries({ queryKey: ["story", story.id] });
			if (story.active_round_id) {
				queryClient.invalidateQueries({
					queryKey: ["round", story.active_round_id],
				});
			}
		} finally {
			setRetrying(false);
		}
	}

	async function handleDelete() {
		setDeleting(true);
		try {
			await deleteStory(story.id);
			queryClient.invalidateQueries({ queryKey: ["stories"] });
			navigate("/stories");
		} finally {
			setDeleting(false);
		}
	}

	const menuActions: MenuAction[] = [
		{
			label: t("stories.detail.done.deleteStory"),
			onClick: () => setShowDeleteConfirm(true),
			destructive: true,
		},
	];

	return (
		<div className="min-h-screen flex flex-col px-6 py-12 max-w-2xl mx-auto">
			<RisographBackground />

			<div className="flex items-center justify-between mb-12">
				<button
					type="button"
					onClick={() => navigate("/stories")}
					className="font-ui text-xs uppercase tracking-widest text-ink/40 hover:text-ink/70 transition-colors"
				>
					{t("stories.detail.done.backToStories")}
				</button>
				<HamburgerMenu actions={menuActions} />
			</div>

			<div className="flex-1 flex flex-col items-center justify-center text-center">
				<p className="font-ui text-xs uppercase tracking-[0.25em] text-coral mb-4">
					{t("stories.detail.failed.heading")}
				</p>
				<p className="font-body italic text-ink/50 text-sm leading-relaxed mb-8 max-w-xs">
					{t("stories.detail.failed.explanation")}
				</p>
				<button
					type="button"
					onClick={handleRetry}
					disabled={retrying}
					className="ink-btn px-8 py-3 font-ui text-sm uppercase tracking-widest disabled:opacity-40"
				>
					{retrying
						? t("stories.detail.failed.retrying")
						: t("stories.detail.failed.tryAgain")}
				</button>
			</div>

			{showDeleteConfirm && (
				<ConfirmDeleteModal
					onConfirm={handleDelete}
					onCancel={() => setShowDeleteConfirm(false)}
					deleting={deleting}
				/>
			)}
		</div>
	);
}

// ── Story update sub-view ─────────────────────────────────────────────────────
function StoryUpdateView({
	story,
	round,
	onContinueCoaching,
}: {
	story: StoryDetail;
	round: Round;
	onContinueCoaching: () => void;
}) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const [editTitle, setEditTitle] = useState(story.title ?? "");
	const [editText, setEditText] = useState(
		story.latest_version?.full_text ?? "",
	);
	const [saving, setSaving] = useState(false);
	const [finishing, setFinishing] = useState(false);
	const [finishConfirming, setFinishConfirming] = useState(false);
	const [showSource, setShowSource] = useState(false);
	const [noteVisible, setNoteVisible] = useState(true);
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	useEffect(() => {
		const el = textareaRef.current;
		if (!el) return;
		el.style.height = "auto";
		el.style.height = `${el.scrollHeight}px`;
	}, [editText]);

	async function handleSave() {
		setSaving(true);
		try {
			await Promise.all([
				updateStoryTitle(story.id, editTitle.trim() || null),
				updateStoryText(story.id, editText),
			]);
			queryClient.invalidateQueries({ queryKey: ["story", story.id] });
		} finally {
			setSaving(false);
		}
	}

	async function handleFinish() {
		setFinishing(true);
		try {
			await Promise.all([
				updateStoryTitle(story.id, editTitle.trim() || null),
				updateStoryText(story.id, editText),
				markDone(story.id),
			]);
			queryClient.invalidateQueries({ queryKey: ["story", story.id] });
			navigate(`/stories/${story.id}`);
		} finally {
			setFinishing(false);
		}
	}

	const version = story.latest_version;

	return (
		<div className="min-h-screen px-6 py-12 max-w-2xl mx-auto">
			<RisographBackground />

			<div className="flex items-baseline gap-3 mb-10">
				{story.title && (
					<p className="font-ui text-[10px] uppercase tracking-widest text-ink/60">
						{story.title}
					</p>
				)}
				<p className="font-ui text-[10px] uppercase tracking-widest text-ink/30">
					{t("stories.detail.storyUpdate.roundComplete", {
						number: round.round_number,
					})}
				</p>
			</div>

			{version?.coaching_feedback && (
				<DismissableNote
					feedback={version.coaching_feedback}
					label={t("stories.detail.storyUpdate.akinNotes")}
					visible={noteVisible}
					onDismiss={() => setNoteVisible(false)}
					onShow={() => setNoteVisible(true)}
				/>
			)}

			<InkDivider className="mb-8" />

			<input
				type="text"
				value={editTitle}
				onChange={(e) => setEditTitle(e.target.value)}
				placeholder={t("stories.detail.storyUpdate.titlePlaceholder")}
				className="w-full bg-transparent font-display text-2xl md:text-3xl font-bold text-ink placeholder-ink/25 outline-none leading-tight mb-4"
			/>

			<div className="mb-6">
				<TagEditor
					storyId={story.id}
					tags={story.tags}
					invalidate={[["story", story.id], ["stories"]]}
				/>
			</div>

			<div className="bg-white/30 backdrop-blur-sm border border-ink/10 rounded-sm p-2 md:p-8 mb-4">
				<textarea
					ref={textareaRef}
					value={editText}
					onChange={(e) => setEditText(e.target.value)}
					placeholder={t("stories.detail.storyUpdate.storyPlaceholder")}
					className="w-full min-h-[240px] bg-transparent font-body text-ink/80 text-lg leading-relaxed outline-none resize-none placeholder-ink/25 overflow-hidden"
				/>
			</div>

			<div className="flex items-center justify-between mb-10">
				<button
					type="button"
					onClick={handleSave}
					disabled={saving}
					className="font-ui text-[10px] uppercase tracking-widest text-ink/35 hover:text-ink/60 transition-colors disabled:opacity-40"
				>
					{saving
						? t("stories.detail.storyUpdate.saving")
						: t("stories.detail.storyUpdate.saveChanges")}
				</button>
				<button
					type="button"
					onClick={() => setShowSource(true)}
					className="font-ui text-[10px] uppercase tracking-widest text-ink/30 hover:text-ink/60 transition-colors"
				>
					{t("stories.detail.sourceMaterial.trigger")} ↗
				</button>
			</div>

			<InkDivider className="mb-8" />

			<div className="flex items-center gap-3">
				{finishConfirming ? (
					<div className="flex items-center gap-2 mr-auto">
						<span className="font-ui text-[10px] uppercase tracking-widest text-ink/40">
							{t("stories.detail.coaching.imDoneConfirm")}
						</span>
						<button
							type="button"
							onClick={() => setFinishConfirming(false)}
							className="border border-ink/20 rounded-sm px-4 py-2 font-ui text-[10px] uppercase tracking-widest text-ink/50 hover:border-ink/40 hover:text-ink/70 transition-colors"
						>
							{t("stories.detail.coaching.imDoneCancel")}
						</button>
						<button
							type="button"
							onClick={handleFinish}
							disabled={finishing}
							className="border border-coral/30 rounded-sm px-4 py-2 font-ui text-[10px] uppercase tracking-widest text-coral/70 hover:border-coral/50 hover:text-coral transition-colors disabled:opacity-40"
						>
							{finishing
								? t("stories.detail.storyUpdate.saving")
								: t("stories.detail.coaching.imDoneYes")}
						</button>
					</div>
				) : (
					<button
						type="button"
						onClick={() => setFinishConfirming(true)}
						className="border border-ink/20 rounded-sm px-6 py-3 font-ui text-xs uppercase tracking-widest text-ink/50 hover:border-ink/40 hover:text-ink/70 transition-colors mr-auto"
					>
						{t("stories.detail.storyUpdate.imDone")}
					</button>
				)}
				<button
					type="button"
					onClick={onContinueCoaching}
					className="ink-btn px-8 py-3 font-ui text-sm uppercase tracking-widest"
				>
					{t("stories.detail.storyUpdate.keepGoing")}
				</button>
			</div>

			{showSource && (
				<SourceMaterialModal
					storyId={story.id}
					onClose={() => setShowSource(false)}
				/>
			)}
		</div>
	);
}

// ── Done sub-view ──────────────────────────────────────────────────────────────
function DoneView({
	story,
	onRestart,
}: {
	story: StoryDetail;
	onRestart: () => void;
}) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const [isEditing, setIsEditing] = useState(false);
	const [editText, setEditText] = useState(
		story.latest_version?.full_text ?? "",
	);
	const [saving, setSaving] = useState(false);
	const [restarting, setRestarting] = useState(false);
	const [showSource, setShowSource] = useState(false);
	const [noteVisible, setNoteVisible] = useState(true);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [deleting, setDeleting] = useState(false);

	const accent = accentForId(story.id);

	const { data: recordingData } = useQuery<AudioUrlResponse>({
		queryKey: ["story", story.id, "recording"],
		queryFn: () => getFinalRecordingUrl(story.id),
		enabled: !!story.final_recording_key,
	});

	async function handleRestart() {
		setRestarting(true);
		try {
			await restartStory(story.id);
			onRestart();
			queryClient.invalidateQueries({ queryKey: ["story", story.id] });
			if (story.active_round_id) {
				queryClient.invalidateQueries({
					queryKey: ["round", story.active_round_id],
				});
			}
		} finally {
			setRestarting(false);
		}
	}

	async function handleDelete() {
		setDeleting(true);
		try {
			await deleteStory(story.id);
			queryClient.invalidateQueries({ queryKey: ["stories"] });
			navigate("/stories");
		} finally {
			setDeleting(false);
		}
	}

	async function handleCopy() {
		const text = [
			story.title ?? t("stories.card.untitled"),
			"",
			isEditing ? editText : (story.latest_version?.full_text ?? ""),
		].join("\n");
		await navigator.clipboard.writeText(text);
	}

	async function handleSave() {
		setSaving(true);
		try {
			await updateStoryText(story.id, editText);
			queryClient.invalidateQueries({ queryKey: ["story", story.id] });
			setIsEditing(false);
		} finally {
			setSaving(false);
		}
	}

	function handleCancel() {
		setEditText(story.latest_version?.full_text ?? "");
		setIsEditing(false);
	}

	const menuActions: MenuAction[] = [
		{
			label: t("stories.detail.done.edit"),
			onClick: () => setIsEditing(true),
			disabled: isEditing,
		},
		{
			label: t("stories.detail.done.copyText"),
			onClick: handleCopy,
		},
		{
			label: t("stories.detail.sourceMaterial.trigger"),
			onClick: () => setShowSource(true),
		},
		{
			label: t("stories.detail.done.answerQuestionsAgain"),
			onClick: handleRestart,
			disabled: restarting,
		},
		{
			label: t("stories.detail.done.beginNew"),
			onClick: () => navigate("/stories/new"),
		},
		{
			label: t("stories.detail.done.deleteStory"),
			onClick: () => setShowDeleteConfirm(true),
			destructive: true,
			dividerBefore: true,
		},
	];

	return (
		<div className="min-h-screen px-6 py-12 max-w-2xl mx-auto">
			<RisographBackground />

			<div className="flex items-center justify-between mb-12">
				<button
					type="button"
					onClick={() => navigate("/stories")}
					className="font-ui text-xs uppercase tracking-widest text-ink/40 hover:text-ink/70 transition-colors"
				>
					{t("stories.detail.done.backToStories")}
				</button>
				<HamburgerMenu actions={menuActions} />
			</div>

			{story.title && (
				<h1 className="font-display text-2xl md:text-4xl font-bold text-ink mb-2 leading-tight">
					{story.title}
				</h1>
			)}

			<p className="font-ui text-xs uppercase tracking-widest text-ink/40 mb-6">
				{new Date(story.created_at).toLocaleDateString(undefined, {
					year: "numeric",
					month: "long",
					day: "numeric",
				})}
			</p>

			<div className="mb-6">
				<TagEditor
					storyId={story.id}
					tags={story.tags}
					invalidate={[["story", story.id], ["stories"]]}
				/>
			</div>

			{story.latest_version?.coaching_feedback && (
				<DismissableNote
					feedback={story.latest_version.coaching_feedback}
					label={t("stories.detail.done.akinNotes")}
					visible={noteVisible}
					onDismiss={() => setNoteVisible(false)}
					onShow={() => setNoteVisible(true)}
				/>
			)}

			{recordingData?.url ? (
				<div className="mb-8">
					<CompactAudioPlayer url={recordingData.url} accent={accent} />
				</div>
			) : (
				<div className="mb-8">
					<button
						type="button"
						onClick={() => navigate(`/stories/${story.id}/rerecord`)}
						className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white transition-opacity hover:opacity-85"
						style={{ background: accent }}
						aria-label="Record your story"
					>
						<svg
							width="12"
							height="12"
							viewBox="-5 0 32 32"
							fill="currentColor"
							aria-hidden="true"
						>
							<g transform="translate(-107.000000, -309.000000)">
								<path d="M118,333 C121.866,333 125,329.866 125,326 L125,316 C125,312.134 121.866,309 118,309 C114.134,309 111,312.134 111,316 L111,326 C111,329.866 114.134,333 118,333 L118,333 Z M129,328 L127,328 C126.089,332.007 122.282,335 118,335 C113.718,335 109.911,332.007 109,328 L107,328 C107.883,332.799 112.063,336.51 117,336.955 L117,339 L116,339 C115.448,339 115,339.448 115,340 C115,340.553 115.448,341 116,341 L120,341 C120.552,341 121,340.553 121,340 C121,339.448 120.552,339 120,339 L119,339 L119,336.955 C123.937,336.51 128.117,332.799 129,328 L129,328 Z" />
							</g>
						</svg>
						<span className="font-ui text-sm">
							{t("stories.detail.done.recordStory")}
						</span>
					</button>
				</div>
			)}

			<div className="bg-white/30 backdrop-blur-sm border border-ink/10 rounded-sm p-4 md:p-8 mb-4">
				{isEditing ? (
					<textarea
						value={editText}
						onChange={(e) => setEditText(e.target.value)}
						className="w-full min-h-[320px] bg-transparent font-body text-ink/80 text-lg leading-relaxed outline-none resize-none"
					/>
				) : (
					<p className="font-body text-ink/80 text-lg leading-relaxed whitespace-pre-wrap">
						{story.latest_version?.full_text ?? ""}
					</p>
				)}
			</div>

			{isEditing && (
				<div className="flex justify-end gap-3 mb-6">
					<button
						type="button"
						onClick={handleCancel}
						className="font-ui text-[10px] uppercase tracking-widest text-ink/35 hover:text-ink/60 transition-colors"
					>
						{t("stories.detail.done.cancel")}
					</button>
					<button
						type="button"
						onClick={handleSave}
						disabled={saving}
						className="font-ui text-[10px] uppercase tracking-widest text-blue hover:text-blue/70 transition-colors disabled:opacity-40"
					>
						{saving
							? t("stories.detail.done.saving")
							: t("stories.detail.done.saveChanges")}
					</button>
				</div>
			)}

			{showSource && (
				<SourceMaterialModal
					storyId={story.id}
					onClose={() => setShowSource(false)}
				/>
			)}

			{showDeleteConfirm && (
				<ConfirmDeleteModal
					onConfirm={handleDelete}
					onCancel={() => setShowDeleteConfirm(false)}
					deleting={deleting}
				/>
			)}
		</div>
	);
}

// ── Root ───────────────────────────────────────────────────────────────────────
export function StoryDetailPage() {
	const { id } = useParams<{ id: string }>();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { onboardingComplete } = useAuth();
	const [seenRoundId, setSeenRoundId] = useState<string | null>(null);

	const {
		data: story,
		isLoading,
		isError,
	} = useQuery<StoryDetail>({
		queryKey: ["story", id],
		queryFn: () => {
			if (!id) {
				throw new Error("Story id is required");
			}
			return getStory(id);
		},
		refetchInterval: (query) => {
			const status = query.state.data?.status;
			return status === "processing" ? 3000 : false;
		},
		enabled: !!id,
	});

	useEffect(() => {
		if (!story || story.status !== "done" || !id) return;
		if (onboardingComplete === false) {
			navigate("/onboarding/celebration", { replace: true });
			return;
		}
		const key = `akin_rerecord_shown_${id}`;
		if (!localStorage.getItem(key)) {
			localStorage.setItem(key, "1");
			navigate(`/stories/${id}/rerecord`, { replace: true });
		}
	}, [story, id, navigate, onboardingComplete]);

	const round = useRoundPolling(story?.active_round_id);

	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<RisographBackground />
				<p className="font-body italic text-ink/40 text-lg">
					{t("stories.detail.loading")}
				</p>
			</div>
		);
	}

	if (isError || !story) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<RisographBackground />
				<p className="font-ui text-sm uppercase tracking-wide text-coral">
					{t("stories.detail.notFound")}
				</p>
			</div>
		);
	}

	if (story.status === "failed") {
		return <FailedView story={story} />;
	}

	if (story.status === "done") {
		return (
			<DoneView
				story={story}
				onRestart={() => setSeenRoundId(story.active_round_id ?? null)}
			/>
		);
	}

	if (round.data?.status === "transcribed") {
		return <TranscriptionPreviewView story={story} round={round.data} />;
	}

	if (
		story.status === "awaiting_response" &&
		round.data?.status === "completed" &&
		round.data.questions?.length
	) {
		if (seenRoundId !== round.data.id) {
			return (
				<StoryUpdateView
					story={story}
					round={round.data}
					onContinueCoaching={() => setSeenRoundId(round.data.id)}
				/>
			);
		}
		return <CoachingView story={story} round={round.data} />;
	}

	return <ProcessingView story={story} />;
}
